import * as React from 'react';

import { Button, Col, Row } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideDot,
  LucidePlusCircle,
  LucideTrash2,
  LucideUser,
} from 'lucide-react';

import { IContact } from '../_state/_types';
import {
  $deleteParticipant,
  $externalParticipants,
  $internalParticipants,
} from '../_state/conversation.state';
import { useTranslations } from '../i18n/useTranslations';

import { ContentBox } from './ContentBox';
import { NewParticipantPopover } from './NewParticipantPopover';

export type ContactRowProps = {
  contact: IContact;
  isSelf?: boolean;
  onDelete: (contact: IContact) => void;
};

export const ParticipantRow = ({
  contact,
  isSelf,
  onDelete,
}: ContactRowProps) => {
  const t = useTranslations();

  return (
    <Row
      key={contact.user.id}
      className="@ParticipantRow rounded-[8px] p-1 pr-2.5 hover:bg-neutral/5 hover:[&_button]:opacity-100"
      gap2
      yStart
    >
      <div
        className={
          'flex size-11 items-center justify-center rounded-[8px] border border-neutral/20 p-px'
        }
      >
        {contact.user.photoUrl ? (
          <img
            src={contact.user.photoUrl ?? ''}
            alt={contact.user.name}
            className={'size-full rounded-[7px] object-cover'}
          />
        ) : (
          <LucideUser className={'size-6 opacity-50'} />
        )}
      </div>
      <Col className={'gap-0.5'}>
        <Row className={'text-sm font-semibold'} gap1>
          <span>{contact.user.name}</span>
          {isSelf && (
            <span className="font-normal text-current/50">
              {t.participants.you}
            </span>
          )}
        </Row>
        <Row gap1 className={'text-sm'}>
          <span className="text-current/50 capitalize">
            {contact.user.custom.organizationName}
          </span>
        </Row>
        {contact.user.custom.email ||
          (contact.user.custom.phone && (
            <Row gap1 className={'text-sm'}>
              {contact.user.custom.email && (
                <a href={`mailto:${contact.user.custom.email}`}>
                  {contact.user.custom.email}
                </a>
              )}
              {contact.user.custom.email && contact.user.custom.phone && (
                <LucideDot className={'size-4 opacity-50'} />
              )}
              {contact.user.custom.phone && (
                <a href={`tel:${contact.user.custom.phone}`}>
                  {contact.user.custom.phone}
                </a>
              )}
            </Row>
          ))}
      </Col>
      {!isSelf && (
        <Button
          className={'my-auto ml-auto opacity-20'}
          icon={<LucideTrash2 />}
          variant="outline"
          color="failure"
          size="icon"
          onClick={() => onDelete(contact)}
        />
      )}
    </Row>
  );
};

export const TabParticipants = () => {
  const t = useTranslations();
  const internalParticipants = useAtomValue($internalParticipants);
  const externalParticipants = useAtomValue($externalParticipants);
  const deleteParticipant = useSetAtom($deleteParticipant);

  const handleDelete = React.useCallback(
    (contact: IContact) => {
      deleteParticipant(contact.user.id);
    },
    [deleteParticipant],
  );

  return (
    <ContentBox className="@TabParticipants p-1">
      <Col className={'h-full gap-4 overflow-y-auto p-1.5 pl-2'}>
        <Col gap2 className={'max-h-full'}>
          <Row className={'text-sm text-current/50 uppercase'} gap2>
            <span>{t.participants.employees}</span>
            <span className={'rounded bg-current/15 px-1.5 py-px'}>
              {internalParticipants.length}
            </span>
            <NewParticipantPopover>
              <Button
                icon={<LucidePlusCircle />}
                color="primary"
                className={'ml-auto h-auto! px-3 py-1.5'}
                size="small"
              >
                {t.participants.addParticipant}
              </Button>
            </NewParticipantPopover>
          </Row>
          <Col gap1>
            {internalParticipants.map((it) => (
              <ParticipantRow
                key={it.user.id}
                contact={it}
                isSelf={it.isSelf}
                onDelete={handleDelete}
              />
            ))}
          </Col>
        </Col>
        {externalParticipants.length > 0 && (
          <Col gap2>
            <Row className={'text-sm text-current/50 uppercase'} gap2>
              <span>{t.participants.external}</span>
              <span className={'rounded bg-current/15 px-1.5 py-px'}>
                {externalParticipants.length}
              </span>
            </Row>
            <Col gap1>
              {externalParticipants.map((it) => (
                <ParticipantRow
                  key={it.user.id}
                  contact={it}
                  onDelete={handleDelete}
                />
              ))}
            </Col>
          </Col>
        )}
      </Col>
    </ContentBox>
  );
};
